import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import type FVector from '@data/geometry/FVector';
import GeomSerializable, { GeomHeaderSchema } from './GeomSerializable';
import { GeometryChunkId } from './GeometryChunkId';

type BaseSchema = {
    version: number;
};
type SchemaV5 = BaseSchema & {
    numberOfTargets: number;
    targets: {
        kind: number;
        numberOfTris: number;
        trisIds: number[];
        numberOfPolygons: number;
        polygons: { points: FVector[] }[];
    }[];
};
type SchemaV15 = BaseSchema & {
    numberOfTargets: number;
    targets: {
        kind: number;
        numberOfPolygons: number;
        polygons: {
            id: number;
            numberOfTris: number;
            trisIds: number[];
            points: FVector[];
        }[];
    }[];
};
type SchemaV16 = BaseSchema & {
    numberOfTargets: number;
    targets: {
        kind: number;
        numberOfPolygons: number;
        polygons: {
            id: number;
            numberOfVertices: number;
            vertices: FVector[];
            numberOfTris: number;
            indices: number[];
            middlePoints: FVector[];
            numberOfPoints: number;
            points: FVector[];
        }[];
    }[];
};
type PreviousSchema = SchemaV5 | SchemaV15 | SchemaV16;
export type LatestTargetsSchema = SchemaV16;

class TargetsSerializer extends GeomSerializable<PreviousSchema, LatestTargetsSchema> {
    protected readonly expectedChunkId = GeometryChunkId.Targets;
    private static readonly LATEST_VERSION = 16;

    protected migrateToLatest(anyPrevious: PreviousSchema): LatestTargetsSchema {
        let migrated: PreviousSchema = anyPrevious;
        while (migrated.version < TargetsSerializer.LATEST_VERSION) {
            const migrate = this.getMigrationForVersion(migrated.version);
            if (!migrate) {
                throw new Error(`Missing Targets migration for version ${migrated.version}`);
            }
            migrated = migrate(migrated);
        }

        return migrated as LatestTargetsSchema;
    }

    protected deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestTargetsSchema {
        let parsed: PreviousSchema;
        if (header.version >= 5 && header.version < 15) {
            parsed = this.deserializeV5(header, reader);
        } else if (header.version === 15) {
            parsed = this.deserializeV15(header, reader);
        } else if (header.version >= 16) {
            parsed = this.deserializeV16(header, reader);
        } else {
            throw new Error(`Unsupported Targets version: ${header.version}. Update your files`);
        }
        return this.migrateToLatest(parsed);
    }

    private getMigrationForVersion(version: number): ((schema: PreviousSchema) => PreviousSchema) | undefined {
        if (version < 15) return (schema) => this.migrateV5ToV15(schema as SchemaV5);
        if (version === 15) return (schema) => this.migrateV15ToV16(schema as SchemaV15);
        return undefined;
    }

    private migrateV5ToV15(schema: SchemaV5): SchemaV15 {
        return {
            version: 15,
            numberOfTargets: schema.numberOfTargets,
            targets: schema.targets.map((target) => ({
                kind: target.kind,
                numberOfPolygons: target.numberOfPolygons,
                polygons: target.polygons.map((polygon, index) => ({
                    id: index,
                    numberOfTris: target.numberOfTris,
                    trisIds: target.trisIds,
                    points: polygon.points,
                })),
            })),
        };
    }

    private migrateV15ToV16(schema: SchemaV15): SchemaV16 {
        return {
            version: 16,
            numberOfTargets: schema.numberOfTargets,
            targets: schema.targets.map((target) => ({
                kind: target.kind,
                numberOfPolygons: target.numberOfPolygons,
                polygons: target.polygons.map((polygon) => ({
                    id: polygon.id,
                    numberOfVertices: polygon.points.length,
                    vertices: polygon.points,
                    numberOfTris: 0, // Map to ground triangles, not needed anymore.
                    indices: [], // Map to ground triangles, not needed anymore.
                    middlePoints: [], // Map to ground triangles, not needed anymore.
                    numberOfPoints: polygon.points.length,
                    points: polygon.points,
                })),
            })),
        };
    }

    private deserializeV5(header: GeomHeaderSchema, reader: BinaryReader): SchemaV5 {
        const targets: SchemaV5['targets'] = [];
        const numberOfTargets = reader.readUInt8();

        for (let g = 0; g < numberOfTargets; g++) {
            const kind = reader.readUInt8();
            const numberOfTris = reader.readUInt16();
            const trisIds = new Array(numberOfTris);
            for (let t = 0; t < numberOfTris; t++) {
                trisIds[t] = reader.readUInt32();
            }

            const numberOfPolygons = reader.readUInt16();
            const polygons: SchemaV5['targets'][number]['polygons'] = [];
            for (let p = 0; p < numberOfPolygons; p++) {
                const numPnts = reader.readUInt32();
                const points: FVector[] = [];
                for (let pc = 0; pc < numPnts; pc++) {
                    points.push(reader.readVec3());
                }
                polygons.push({ points });
            }

            targets.push({ kind, numberOfTris, trisIds, numberOfPolygons, polygons });
        }
        return { version: header.version, numberOfTargets, targets };
    }

    private deserializeV15(header: GeomHeaderSchema, reader: BinaryReader): SchemaV15 {
        const targets: SchemaV15['targets'] = [];
        const numberOfTargets = reader.readUInt8();
        for (let g = 0; g < numberOfTargets; g++) {
            const kind = reader.readUInt8();
            const numberOfPolygons = reader.readUInt16();
            const polygons: SchemaV15['targets'][number]['polygons'] = [];
            for (let p = 0; p < numberOfPolygons; p++) {
                const id = reader.readUInt8();
                const numberOfTris = reader.readUInt16();
                const trisIds = new Array(numberOfTris);
                for (let t = 0; t < numberOfTris; t++) {
                    trisIds[t] = reader.readUInt32();
                }
                const numberOfPoints = reader.readUInt32();
                const points = new Array(numberOfPoints);
                for (let pc = 0; pc < numberOfPoints; pc++) {
                    points[pc] = reader.readVec3();
                }
                polygons.push({ id, numberOfTris, trisIds, points });
            }
            targets.push({ kind, numberOfPolygons, polygons });
        }
        return { version: header.version, numberOfTargets, targets };
    }

    private deserializeV16(header: GeomHeaderSchema, reader: BinaryReader): SchemaV16 {
        const targets: SchemaV16['targets'] = [];
        const numberOfTargets = reader.readUInt8();

        for (let g = 0; g < numberOfTargets; g++) {
            const kind = reader.readUInt8();
            const numberOfPolygons = reader.readUInt16();
            const polygons: SchemaV16['targets'][number]['polygons'] = [];

            for (let p = 0; p < numberOfPolygons; p++) {
                const id = reader.readUInt8();
                const numberOfVertices = reader.readUInt16();
                const vertices = new Array(numberOfVertices);
                for (let v = 0; v < numberOfVertices; v++) {
                    vertices[v] = reader.readVec3();
                }

                const numberOfTris = reader.readUInt16();
                const indices = new Array(numberOfTris * 3);
                for (let i = 0; i < indices.length; i++) {
                    indices[i] = reader.readUInt32();
                }

                const middlePoints = new Array(numberOfTris);
                for (let i = 0; i < numberOfTris; i++) {
                    middlePoints[i] = reader.readVec3();
                }

                const numberOfPoints = reader.readUInt32();
                const points = new Array(numberOfPoints);
                for (let i = 0; i < numberOfPoints; i++) {
                    points[i] = reader.readVec3();
                }

                polygons.push({
                    id,
                    numberOfVertices,
                    vertices,
                    numberOfTris,
                    indices,
                    middlePoints,
                    numberOfPoints,
                    points,
                });
            }

            targets.push({ kind, numberOfPolygons, polygons });
        }

        return { version: header.version, numberOfTargets, targets };
    }
}

export default TargetsSerializer;
